const CONSENT_STORAGE_KEY = "stu_cookie_consent";
const CONSENT_VERSION = 1;

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID || "G-MMQ5TL5EC0";
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || "26915837604691768";

const defaultConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
  version: CONSENT_VERSION,
  updatedAt: null,
};

let googleAnalyticsLoaded = false;
let metaPixelLoaded = false;

export const trackingConfig = {
  gaMeasurementId: GA_MEASUREMENT_ID,
  metaPixelId: META_PIXEL_ID,
};

export function getStoredConsent() {
  if (typeof window === "undefined") return null;

  try {
    const savedConsent = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!savedConsent) return null;

    return normalizeConsent(JSON.parse(savedConsent));
  } catch (error) {
    return null;
  }
}

export function hasStoredConsent() {
  return Boolean(getStoredConsent());
}

export function saveConsent(consent) {
  if (typeof window === "undefined") return;

  const normalizedConsent = normalizeConsent({
    ...consent,
    updatedAt: new Date().toISOString(),
  });

  window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(normalizedConsent));
  applyTrackingConsent(normalizedConsent);
  window.dispatchEvent(
    new CustomEvent("stuCookieConsentChanged", { detail: normalizedConsent })
  );
}

export function normalizeConsent(consent) {
  return {
    ...defaultConsent,
    ...consent,
    necessary: true,
    analytics: Boolean(consent?.analytics),
    marketing: Boolean(consent?.marketing),
    version: CONSENT_VERSION,
  };
}

export function applyTrackingConsent(consent = getStoredConsent()) {
  const normalizedConsent = normalizeConsent(consent);

  if (normalizedConsent.analytics) {
    loadGoogleAnalytics();
    if (window.gtag) {
      window.gtag("consent", "update", {
        analytics_storage: "granted",
        ad_storage: "denied",
        ad_user_data: "denied",
        ad_personalization: "denied",
      });
    }
  } else if (window.gtag) {
    window.gtag("consent", "update", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
  }

  if (normalizedConsent.marketing) {
    loadMetaPixel();
    if (window.fbq) {
      window.fbq("consent", "grant");
    }
  } else if (window.fbq) {
    window.fbq("consent", "revoke");
  }
}

export function trackPageView(path = getCurrentPath()) {
  const consent = getStoredConsent();

  if (consent?.analytics && window.gtag) {
    window.gtag("event", "page_view", {
      page_path: path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }

  if (consent?.marketing && window.fbq) {
    window.fbq("track", "PageView");
  }
}

export function trackEvent(eventName, parameters = {}) {
  const consent = getStoredConsent();
  const { metaEventName, metaParameters, ...gaParameters } = parameters;

  if (consent?.analytics && window.gtag) {
    window.gtag("event", eventName, compactTrackingParams(gaParameters));
  }

  if (consent?.marketing && window.fbq && metaEventName) {
    window.fbq("track", metaEventName, compactTrackingParams(metaParameters || {}));
  }
}

export function trackSignup({ method = "email", source = "registration" } = {}) {
  trackEvent("sign_up", {
    method,
    source,
    metaEventName: "CompleteRegistration",
    metaParameters: {
      status: "completed",
      content_name: "account_registration",
    },
  });
}

export function trackLogin({ method = "email", source = "login" } = {}) {
  trackEvent("login", {
    method,
    source,
  });
}

export function trackProfileComplete({ source = "profile_completion" } = {}) {
  trackEvent("profile_complete", {
    source,
  });
}

export function trackLead({ leadType = "contact", source = "website" } = {}) {
  trackEvent("generate_lead", {
    lead_type: leadType,
    source,
    metaEventName: "Lead",
    metaParameters: {
      content_name: leadType,
      content_category: source,
    },
  });
}

export function trackHostContactStarted({
  studio,
  studioId,
  source = "studio_profile",
  requiresAuth = false,
} = {}) {
  const item = buildStudioItem({ studio, studioId, source });
  if (!item.item_id) return;

  trackEvent("host_contact_start", {
    studio_id: item.item_id,
    item_name: item.item_name,
    requires_auth: Boolean(requiresAuth),
    source,
  });
}

export function trackHostInquirySent({
  studio,
  studioId,
  source = "studio_message",
  hasRequestedDate = false,
  isFlexible = false,
} = {}) {
  const item = buildStudioItem({ studio, studioId, source });
  if (!item.item_id) return;

  trackEvent("generate_lead", {
    lead_type: "studio_inquiry",
    studio_id: item.item_id,
    has_requested_date: Boolean(hasRequestedDate),
    is_flexible: Boolean(isFlexible),
    source,
    metaEventName: "Lead",
    metaParameters: {
      content_name: "studio_inquiry",
      content_category: source,
    },
  });
}

export function trackSearchPerformed({
  activity = "",
  location = "",
  resultsCount,
  source = "search_page",
  filtersCount = 0,
} = {}) {
  const sanitizedActivity = sanitizeSearchText(activity);
  const sanitizedLocation = sanitizeSearchText(location);
  const searchTerm = [sanitizedActivity, sanitizedLocation].filter(Boolean).join(" in ");
  if (!searchTerm) return;

  trackEvent("search", {
    search_term: searchTerm,
    search_activity: sanitizedActivity,
    search_location: sanitizedLocation,
    results_count: toSafeNumber(resultsCount),
    filters_count: toSafeNumber(filtersCount),
    source,
    metaEventName: "Search",
    metaParameters: {
      search_string: searchTerm,
      content_category: sanitizedActivity || "studio_search",
    },
  });
}

export function trackStudioSelected({ studio, source = "search_results", index } = {}) {
  const studioId = getStudioId(studio);
  if (!studioId) return;

  trackEvent("select_content", {
    content_type: "studio",
    content_id: studioId,
    item_name: getStudioName(studio),
    item_list_id: source,
    item_list_name: source,
    index: toSafeNumber(index),
    source,
  });
}

export function trackStudioViewed({ studio, source = "studio_profile" } = {}) {
  const item = buildStudioItem({ studio, source });
  if (!item.item_id) return;

  const value = getStudioPrice(studio);

  trackEvent("view_item", {
    currency: "EUR",
    value,
    items: [item],
    source,
    metaEventName: "ViewContent",
    metaParameters: {
      content_ids: [item.item_id],
      content_name: item.item_name,
      content_type: "product",
      currency: "EUR",
      value,
    },
  });
}

export function trackBookingStarted({ studio, studioId, source = "booking_flow", value } = {}) {
  const item = buildStudioItem({ studio, studioId, source });
  if (!item.item_id) return;

  const normalizedValue = toSafeNumber(value ?? getStudioPrice(studio));

  trackEvent("begin_checkout", {
    currency: "EUR",
    value: normalizedValue,
    items: [item],
    source,
    metaEventName: "InitiateCheckout",
    metaParameters: {
      content_ids: [item.item_id],
      content_name: item.item_name,
      content_type: "product",
      currency: "EUR",
      value: normalizedValue,
      num_items: 1,
    },
  });
}

export function trackWishlistAdded({ studio, studioId, source = "wishlist" } = {}) {
  const item = buildStudioItem({ studio, studioId, source });
  if (!item.item_id) return;

  trackEvent("add_to_wishlist", {
    currency: "EUR",
    value: getStudioPrice(studio),
    items: [item],
    source,
    metaEventName: "AddToWishlist",
    metaParameters: {
      content_ids: [item.item_id],
      content_name: item.item_name,
      content_type: "product",
      currency: "EUR",
      value: getStudioPrice(studio),
    },
  });
}

export function trackBookingStepViewed({
  studio,
  studioId,
  stepIndex,
  stepName,
  source = "booking_flow",
} = {}) {
  const resolvedStudioId = studioId || getStudioId(studio);
  if (!resolvedStudioId) return;

  trackEvent("booking_step_view", {
    studio_id: resolvedStudioId,
    booking_step: toSafeNumber(stepIndex),
    booking_step_name: safeText(stepName),
    pricing_mode: safeText(studio?.pricingMode),
    source,
  });
}

export function trackPaymentInfoAdded({ payload, reservation, source = "booking_checkout" } = {}) {
  const item = buildStudioItem({ studioId: payload?.studioID, source });
  if (!item.item_id) return;

  const value = getReservationValue(reservation);

  trackEvent("add_payment_info", {
    currency: "EUR",
    value,
    payment_type: "card",
    items: [item],
    source,
    metaEventName: "AddPaymentInfo",
    metaParameters: {
      content_ids: [item.item_id],
      content_type: "product",
      currency: "EUR",
      value,
    },
  });
}

export function trackBookingPurchased({ booking, payload, reservation, source = "booking_checkout" } = {}) {
  const transactionId = booking?.id || reservation?.paymentIntentID || reservation?.slotID;
  const item = buildStudioItem({ studioId: payload?.studioID, source });
  if (!transactionId || !item.item_id) return;

  const value = getReservationValue(reservation);

  trackEvent("purchase", {
    transaction_id: transactionId,
    currency: "EUR",
    value,
    items: [item],
    source,
    metaEventName: "Purchase",
    metaParameters: {
      order_id: transactionId,
      content_ids: [item.item_id],
      content_type: "product",
      currency: "EUR",
      value,
    },
  });
}

export function trackStudioCreationStep({ stepIndex, stepName, source = "studio_creation" } = {}) {
  trackEvent("studio_create_step", {
    step_index: toSafeNumber(stepIndex),
    step_name: safeText(stepName),
    source,
  });
}

export function trackStudioCreationComplete({ studioId, pricingMode, source = "studio_creation" } = {}) {
  trackEvent("studio_create_complete", {
    studio_id: safeText(studioId),
    pricing_mode: safeText(pricingMode),
    source,
    metaEventName: "Lead",
    metaParameters: {
      content_name: "studio_created",
      content_category: "host_onboarding",
    },
  });
}

export function trackProviderCreationIntent({
  creationType,
  source = "services_studios",
} = {}) {
  const normalizedType = safeText(creationType);
  if (!normalizedType) return;

  trackEvent("provider_create_intent", {
    creation_type: normalizedType,
    source,
  });
}

export function trackServiceCreationStep({
  stepIndex,
  stepName,
  creativeType,
  source = "service_creation",
} = {}) {
  trackEvent("service_create_step", {
    step_index: toSafeNumber(stepIndex),
    step_name: safeText(stepName),
    creative_type: safeText(creativeType),
    source,
  });
}

export function trackServiceCreationComplete({
  creativeId,
  studioId,
  creativeType,
  source = "service_creation",
} = {}) {
  const resolvedCreativeId = safeText(creativeId);
  if (!resolvedCreativeId) return;

  trackEvent("service_create_complete", {
    creative_id: resolvedCreativeId,
    studio_id: safeText(studioId),
    creative_type: safeText(creativeType),
    source,
    metaEventName: "Lead",
    metaParameters: {
      content_name: "creative_service_created",
      content_category: "host_onboarding",
    },
  });
}

export function trackCreativeProfilePublished({
  creativeId,
  creativeType,
  source = "creative_profile",
} = {}) {
  const resolvedCreativeId = safeText(creativeId);
  if (!resolvedCreativeId) return;

  trackEvent("creative_profile_publish", {
    creative_id: resolvedCreativeId,
    creative_type: safeText(creativeType),
    source,
  });
}

export function trackBookingStatusChanged({
  booking,
  status,
  source = "booking_management",
} = {}) {
  const resolvedStatus = safeText(status || booking?.status);
  if (!resolvedStatus) return;

  trackEvent("booking_status_change", {
    booking_status: resolvedStatus,
    studio_id: safeText(booking?.studioID || booking?.Studio?.id),
    value: toSafeNumber(booking?.totalPrice || booking?.totalStudioPrice),
    currency: "EUR",
    source,
  });
}

export function trackPayoutSetupStarted({
  source = "payout_setup",
  hasAccount,
} = {}) {
  trackEvent("payout_setup_start", {
    has_account: hasAccount === undefined ? undefined : Boolean(hasAccount),
    source,
  });
}

export function trackPayoutSetupReturned({ source = "stripe_return" } = {}) {
  trackEvent("payout_setup_return", {
    source,
  });
}

export function trackStudioUpdated({
  studioId,
  section,
  source = "studio_editor",
} = {}) {
  const resolvedStudioId = safeText(studioId);
  if (!resolvedStudioId) return;

  trackEvent("studio_update", {
    studio_id: resolvedStudioId,
    section: safeText(section),
    source,
  });
}

export function trackStudioArchived({ studioId, source = "studio_editor" } = {}) {
  const resolvedStudioId = safeText(studioId);
  if (!resolvedStudioId) return;

  trackEvent("studio_archive", {
    studio_id: resolvedStudioId,
    source,
  });
}

export function trackReviewSubmitted({
  reviewType,
  rating,
  source = "booking_details",
} = {}) {
  const resolvedReviewType = safeText(reviewType);
  const resolvedRating = toSafeNumber(rating);
  if (!resolvedReviewType && resolvedRating === undefined) return;

  trackEvent("review_submit", {
    review_type: resolvedReviewType,
    rating: resolvedRating,
    source,
  });
}

function loadGoogleAnalytics() {
  if (!GA_MEASUREMENT_ID || googleAnalyticsLoaded) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  window.gtag("consent", "default", {
    analytics_storage: "granted",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
  });
  window.gtag("js", new Date());
  window.gtag("config", GA_MEASUREMENT_ID, {
    send_page_view: false,
    anonymize_ip: true,
  });

  appendScript(
    "stu-google-analytics",
    `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`
  );
  googleAnalyticsLoaded = true;
}

function loadMetaPixel() {
  if (!META_PIXEL_ID || metaPixelLoaded) return;

  window.fbq = window.fbq || function fbq() {
    if (window.fbq.callMethod) {
      window.fbq.callMethod.apply(window.fbq, arguments);
    } else {
      window.fbq.queue.push(arguments);
    }
  };

  if (!window._fbq) window._fbq = window.fbq;
  window.fbq.push = window.fbq;
  window.fbq.loaded = true;
  window.fbq.version = "2.0";
  window.fbq.queue = window.fbq.queue || [];

  appendScript("stu-meta-pixel", "https://connect.facebook.net/en_US/fbevents.js");
  window.fbq("init", META_PIXEL_ID);
  metaPixelLoaded = true;
}

function appendScript(id, src) {
  if (document.getElementById(id)) return;

  const script = document.createElement("script");
  script.id = id;
  script.async = true;
  script.src = src;
  document.head.appendChild(script);
}

function getCurrentPath() {
  if (typeof window === "undefined") return "/";
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function compactTrackingParams(value) {
  if (Array.isArray(value)) {
    return value
      .map(compactTrackingParams)
      .filter((entry) => entry !== undefined && entry !== null);
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  return Object.entries(value).reduce((acc, [key, entry]) => {
    const compactedEntry = compactTrackingParams(entry);
    const isEmptyString = typeof compactedEntry === "string" && compactedEntry.trim() === "";
    const isEmptyArray = Array.isArray(compactedEntry) && compactedEntry.length === 0;
    if (compactedEntry === undefined || compactedEntry === null || isEmptyString || isEmptyArray) {
      return acc;
    }
    acc[key] = compactedEntry;
    return acc;
  }, {});
}

function safeText(value, maxLength = 100) {
  if (value === undefined || value === null) return "";
  return String(value).trim().slice(0, maxLength);
}

function sanitizeSearchText(value) {
  const text = safeText(value, 120);
  if (!text) return "";

  return text
    .replace(/\b[^\s@]+@[^\s@]+\.[^\s@]+\b/g, "")
    .replace(/\b(?:https?:\/\/|www\.)\S+\b/gi, "")
    .replace(/\b\+?\d[\d\s()./-]{6,}\d\b/g, "")
    .replace(
      /\b\d{1,5}\s+[A-Za-zÄÖÜäöüß.\-\s]{2,40}(?:straße|strasse|str\.|street|weg|allee|platz|ring|gasse|ufer|damm|road|rd\.|ave|avenue|lane|ln\.)\b/gi,
      ""
    )
    .replace(
      /\b[A-Za-zÄÖÜäöüß.\-\s]{2,40}(?:straße|strasse|str\.|street|weg|allee|platz|ring|gasse|ufer|damm|road|rd\.|ave|avenue|lane|ln\.)\s+\d{1,5}\w?\b/gi,
      ""
    )
    .replace(/\s+/g, " ")
    .trim();
}

function toSafeNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : undefined;
}

function getStudioId(studio) {
  return safeText(studio?.id || studio?._id || studio?.studioID || studio?.studioId);
}

function getStudioName(studio) {
  return safeText(studio?.StudioName || studio?.name || "Studio");
}

function getStudioPrice(studio) {
  const price =
    studio?.pricePerHour ??
    studio?.lowestPrice ??
    studio?.totalPrice ??
    studio?.totalStudioPrice;
  return toSafeNumber(price);
}

function buildStudioItem({ studio, studioId, source } = {}) {
  const resolvedStudioId = safeText(studioId) || getStudioId(studio);
  return {
    item_id: resolvedStudioId,
    item_name: getStudioName(studio) || "Studio",
    item_category: Array.isArray(studio?.studioTypes)
      ? safeText(studio.studioTypes.join(", "))
      : safeText(studio?.pricingMode || "studio"),
    item_list_id: safeText(source),
    item_list_name: safeText(source),
    price: getStudioPrice(studio),
    quantity: 1,
  };
}

function getReservationValue(reservation) {
  const amountCents = toSafeNumber(reservation?.totalAmountCents);
  if (amountCents !== undefined) return amountCents / 100;
  return toSafeNumber(reservation?.totalPrice);
}
